import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { animated, useSpring } from 'react-spring';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useMeasure from '~/hooks/useMeasure';
import { isProduction } from '~/util';
import { MASTER_ACCOUNT_ID } from '~/util/constants';
import { Heading5, Label } from '~/components/atom/Typography';
import Icon from '~/components/atom/Icon';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';

type StageName = 'dev' | 'prod';

const text = {
  devTools: 'Dev tools',
  isMasterAccount: 'Master account',
};

const DevTools = () => {
  const currentAccount = useCurrentAccount();
  const [isExpanded, setIsExpanded] = useState(false);
  const { ref, bounds } = useMeasure();
  const STAGE_NAME = process.env.STAGE_NAME as StageName;

  const { width, opacity } = useSpring({
    from: { width: 0, opacity: 0 },
    to: {
      width: isExpanded ? bounds.width : 0,
      opacity: isExpanded ? 1 : 0,
    },
    config: {
      tension: 1000,
      clamp: true,
    },
  });

  const isDev = isProduction() !== true;
  if (!isDev) return null;

  const content = (
    <ContentContainer>
      <JustificationContainer gap="xxs" align="center">
        <Icon name="debug" color={{ group: 'white' }} />
        <Heading5 size="base" margin={[null]} color={{ group: 'white' }}>
          {text.devTools}
        </Heading5>
      </JustificationContainer>
      {currentAccount.id === MASTER_ACCOUNT_ID && (
        <JustificationContainer gap="xxs" align="center">
          <Icon color={{ group: 'info' }} name="info" />{' '}
          <Label color={{ group: 'info' }} margin={[null]}>
            {text.isMasterAccount}
          </Label>
        </JustificationContainer>
      )}
      <CopyTextBlock label="Account ID" textToCopy={currentAccount.id} />
      <CopyTextBlock label="Stage" textToCopy={STAGE_NAME} />
    </ContentContainer>
  );

  return (
    <Container gap="xxs" align="center" direction="row-reverse">
      <Button
        appearance="tertiary"
        ghost
        icon={isExpanded ? 'chevron-left' : 'chevron-right'}
        onClick={() => setIsExpanded(prev => !prev)}
        style={{ alignSelf: 'flex-start' }}
      />
      <animated.div
        style={{
          width,
          opacity,
          overflow: 'hidden',
        }}
      >
        {content}
      </animated.div>
      <HiddenMeasureContainer ref={ref}>{content}</HiddenMeasureContainer>
    </Container>
  );
};

const Container = styled(JustificationContainer)(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.space('xs')};
    right: ${theme.space('s')};
    z-index: ${theme.getTokens().zIndex.minimum};
    pointer-events: all;
  `,
);

const ContentContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space('xxs')};
    white-space: nowrap;
    padding: ${theme.space('xs')};
    border-radius: ${theme.getTokens().border.radius.base};
    background-color: ${theme.color('primary')};
  `,
);

/**
 * This container is used to measure the width of the content container
 * so that we can animate the width of the container
 */
const HiddenMeasureContainer = styled.div(
  () => css`
    position: absolute;
    visibility: hidden;
    height: 0;
    overflow: hidden;
    white-space: nowrap;
  `,
);

export default DevTools;
